import NewLayout from "@components/new-ui/Layout";
import React from "react";
import { CasestudyCard, CasestudyCardProps } from ".";
import { CaseStudyHerosection, CaseStudyInformation, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const casestudyCardsDetails: CasestudyCardProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/til-limited/til-limited.png",
        title: "TIL Limited strengthens customer engagement and drives growth with Gallabox",
        sector: "Manufacturing",
        externalLink: "/case-study/til-limited",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/khivraj-elixir/khivraj-elixir.png",
        title: "Khivraj Elixir accelerates lead conversion with Gallabox",
        sector: "Automobiles",
        externalLink: "/case-study/khivraj-elixir",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/nura/nura-hero.png",
        title: "How NURA Ai Health Screening Simplified Operations with Gallabox",
        sector: "Healthcare",
        externalLink: "/case-study/nura",
    },
];

const Inlingua: React.FC = () => {
    return (
        <NewLayout
            title="Inlingua scales student engagement and doubles growth with Gallabox | Case Study"
            description="Learn how Inlingua improved lead generation, automated student communication, and doubled business growth using Gallabox's WhatsApp automation tools."
            canonical="https://gallabox.com/case-study/inlingua"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/inlingua/inlingua-og.png" }}
        >
            <CaseStudyHerosection
                title="Inlingua doubles growth by streamlining student engagement with Gallabox"
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/inlingua/inlingua.png"}
                redirectUrl={"/case-study"}
            />
            <StatsInfo
                statsInfo={[
                    { percentage: "2x", label: "overall business growth" },
                    { percentage: "50%", label: "increase in lead generation" },
                    { percentage: "50%", label: "reduction in marketing spend" },
                ]}
            />

            <section className="max-w-7xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[{ label: "Category", value: ["Education", "Administration Programs"] }]}
                            product={{
                                title: "Features Used",
                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-chatbot", label: "Chatbots" },
                                    { link: "https://gallabox.com", label: "WhatsApp Forms" },
                                    { link: "https://gallabox.com/whatsapp-flows", label: "WhatsApp Flows" },
                                    { link: "https://gallabox.com/whatsapp-broadcast", label: "Broadcast" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12 ">
                        <section id="About Inlingua">
                            <CaseStudyInformation subTitle={"About Inlingua"} className="mt-0" />
                            <p className="mt-2">
                                Founded on the belief that language is best learned through practice, Inlingua has built its reputation as one of the leading names in professional
                                language training. With its unique speak-to-learn methodology, structured materials, and a global network of centers, Inlingua delivers
                                high-quality, practical training that emphasizes communication and real-world application.
                            </p>
                            <p className="mt-5">
                                The organization&apos;s core teaching philosophy revolves around keeping learners engaged through conversation, building on existing knowledge, and
                                tailoring lessons to individual student needs — all while using the target language directly. Consistency, innovation, and adaptability have long
                                defined Inlingua&apos;s mission: making language learning accessible, effective, and impactful.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation subTitle={"Challenges before Gallabox"} className="mt-0" />

                            <p className="mt-2">As more students searched for flexible training solutions, Inlingua faced growing challenges in customer communication:</p>
                            <FeatureList
                                List={[
                                    {
                                        listItem: "Difficulty reaching students quickly and effectively",
                                        nestedListItem: [
                                            "The team often relied on social media platforms and cold outreach to connect with learners. But these methods lacked consistency and scalability, making it harder to nurture prospects into paying students.",
                                        ],
                                    },
                                    {
                                        listItem: "Manual follow-ups consuming bandwidth",
                                        nestedListItem: [
                                            "Every inquiry had to be tracked and responded to manually, leading to delays, missed opportunities, and an overall slowdown in student onboarding.",
                                        ],
                                    },
                                    {
                                        listItem: "Limited personalization at scale",
                                        nestedListItem: [
                                            "Students expected instant, personalized interaction, but with fragmented systems in place, providing a seamless experience across each touchpoint became increasingly difficult.",
                                        ],
                                    },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                            <p className="mt-5">The result: Inlingua risked losing valuable prospects simply because it couldn&apos;t engage them fast enough.</p>
                            <CaseStudyInformation subTitle={"Initial solutions tried"} className="mt-10" />
                            <p className="mt-2">Before Gallabox, Inlingua leaned heavily on:</p>
                            <FeatureList
                                List={[
                                    {
                                        listItem: "Cold outreach to generate leads, which proved time-intensive with low conversion.",
                                    },
                                    {
                                        listItem: "Social media platforms to drive awareness and communication, but these lacked structure and scalability.",
                                    },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                            <p className="mt-5">
                                While these methods delivered some traction, they demanded too much manual intervention and didn&apos;t solve the core challenge: engaging students
                                instantly and effectively at scale.
                            </p>
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation subTitle={"Why Inlingua chose Gallabox"} className="mt-0" />
                            <p className="mt-2">
                                Initially referred to Gallabox through industry peers, Naser and the Inlingua team decided to evaluate the platform against traditional solutions.
                                Unlike social media-driven outreach, Gallabox provided a consolidated, direct, and scalable approach to student engagement. With Gallabox, Inlingua
                                could:
                            </p>

                            <FeatureList
                                List={[
                                    {
                                        listItem: "Generate more leads by instantly engaging interested learners through automated WhatsApp workflows.",
                                    },
                                    {
                                        listItem: "Streamline student communication by bringing all interactions into one platform, ensuring no inquiries slipped through.",
                                    },
                                    {
                                        listItem:
                                            "Automate repetitive support such as FAQs, class schedules, and enrollment processes — saving time for staff to focus on personalized guidance.",
                                    },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                            <p className="mt-5">
                                Features like chatbots, WhatsApp Forms, Flows, and Broadcasts gave Inlingua both efficiency and flexibility. The platform offered the right mix of
                                automation without making communication feel robotic or impersonal—essential for an education brand built on meaningful human interaction.
                            </p>
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation subTitle={"Impact on business growth"} className="mt-0" />
                            <p className="mt-2">The difference was clear and measurable. With Gallabox:</p>

                            <FeatureList
                                List={[
                                    {
                                        listItem: "Business growth doubled (2x impact), showing how improved communication directly fueled scale.",
                                    },
                                    {
                                        listItem: "Lead generation shot up by 50%, as more inquiries were captured and nurtured through WhatsApp.",
                                    },
                                    {
                                        listItem: "Employee productivity rose by 50%, since the team no longer spent hours stuck in repetitive messaging.",
                                    },
                                    {
                                        listItem: "Marketing and advertising spend dropped by 50%, with automation delivering better returns at lower costs.",
                                    },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <p className="mt-5">
                                Gallabox not only helped Inlingua connect faster with potential students but also gave them a competitive edge, ensuring learners got timely,
                                consistent, and supportive communication at every step of the journey.
                            </p>

                            <CaseStudyInformation subTitle={"Turning conversations into enrollments"} className="mt-12" />
                            <p className="mt-2">
                                Language learning is deeply personal, and every student wants to feel supported along their journey. By adopting Gallabox, Inlingua transformed its
                                communication from being resource-heavy and fragmented into a seamless, automated, student-first experience.
                            </p>
                            <p className="mt-5">
                                Now, what used to be hours of manual follow-ups is handled in minutes, freeing the team to do what they do best — help learners unlock new languages
                                and ideas. Gallabox has become more than just a tech tool; it&apos;s a growth partner helping Inlingua scale while staying true to its values.
                            </p>
                            <CaseStudyInformation subTitle={"Looking forward"} className="mt-12" />
                            <p className="mt-2">
                                With student expectations evolving, Inlingua plans to deepen its use of automation flows to streamline enrollment even further, personalize learner
                                onboarding, and support more students across regions. The goal is clear: expand their impact while ensuring every learner feels their journey is
                                supported, structured, and rewarding.
                            </p>
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <div className="max-w-6xl mx-auto">
                <section>
                    <h3 className="text-gray-900 mb-10 mt-5 text-left ml-4">Check out other customer stories</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto gap-10 mb-20">
                        {casestudyCardsDetails?.map((data, index) => (
                            <CasestudyCard key={index} {...data} />
                        ))}
                    </div>
                </section>
            </div>
        </NewLayout>
    );
};

export default Inlingua;
